/**
 * Right-rail outline — a compact, depth-aware list of every step in the
 * active pipeline (including nested loop bodies). Clicking an entry scrolls
 * the corresponding step row into view. Always visible alongside the
 * timeline so a long pipeline stays scannable.
 */
import React, { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { StepIssue, TransformStep } from "stores/queryBuilderStore"
import { stepSummary } from "./stepSummary"
import { STEP_TYPE_ICONS, STEP_TYPE_LABELS } from "./stepTypeConfig"
import { flattenForOutline, scrollToStep } from "./outlineUtils"

interface PipelineOutlineProps {
  steps: TransformStep[]
  issues: Map<string, StepIssue[]>
}

const PipelineOutline: React.FC<PipelineOutlineProps> = ({ steps, issues }) => {
  const rows = useMemo(() => flattenForOutline(steps), [steps])
  if (rows.length === 0) return null
  return (
    <div className="qc-outline">
      <div className="qc-preview__header">
        <span className="qc-preview__title">Outline ({rows.length})</span>
        <span className="qc-preview__hint">Click to jump</span>
      </div>
      <ol className="qc-outline__list">
        {rows.map(({ step, depth, ordinal }) => {
          const summary = stepSummary(step)
          const stepIssues = issues.get(step.id)
          const severity = stepIssues?.some((i) => i.severity === "error")
            ? "error"
            : stepIssues && stepIssues.length > 0
            ? "warn"
            : undefined
          return (
            <li
              key={step.id}
              className="qc-outline__item"
              style={{ paddingLeft: 8 + depth * 14 }}
              data-depth={depth}
              data-severity={severity}
            >
              <button
                type="button"
                className="qc-outline__btn"
                onClick={() => scrollToStep(step.id)}
                title={STEP_TYPE_LABELS[step.type]}
              >
                <span className="qc-outline__ordinal">{ordinal}</span>
                <FontAwesomeIcon
                  icon={STEP_TYPE_ICONS[step.type]}
                  className="qc-outline__icon"
                />
                <span className="qc-outline__summary">
                  {summary.map((tok, i) =>
                    tok.kind === "mono" ? (
                      <span key={i} className="qc-mono qc-outline__mono">
                        {tok.value}
                      </span>
                    ) : (
                      <span key={i}>{tok.value}</span>
                    )
                  )}
                </span>
                {severity && (
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="qc-outline__warn"
                    data-severity={severity}
                  />
                )}
              </button>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default PipelineOutline
